import React, { useRef, useState } from 'react';
import { Box, Button, Menu, MenuItem, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined';
import SpellcheckIcon from '@mui/icons-material/Spellcheck';
import TranslateIcon from '@mui/icons-material/Translate';
import { PRES } from '../chrome';
import { COPY } from '../presentationCopy';
import { ChartKind, SlideObject, SlideObjectStyle } from '../types';
import {
  ChartBarIcon,
  ChartColumnIcon,
  ChartLegendIcon,
  ChartLineIcon,
  ChartPieIcon,
  ChartValuesIcon,
  DashedLineIcon,
  DottedLineIcon,
  InsertButton,
  InsertFileIcon,
  InsertImageIcon,
  InsertShapeIcon,
  InsertSymbolIcon,
  InsertTextIcon,
  ShapeEllipseIcon,
  ShapeRectangleIcon,
  ShapeRoundedRectangleIcon,
  ShapeTriangleIcon,
  SolidLineIcon,
  TableAddColumnIcon,
  TableAddRowIcon,
  TableHeaderIcon,
  TableRemoveColumnIcon,
  TableRemoveRowIcon,
  ToolbarDivider,
  ToolbarIconButton,
  toolbarToggleSx,
} from './toolbarIcons';
import SelectionToolbar from './SelectionToolbar';
import { hasEditableText } from './shapeFrame';
import { ListKind } from './listHtml';
import { TextStyleStep } from './layerList';
import { normalizeTable, TABLE_MAX_COLS, TABLE_MAX_ROWS } from './tableModel';

export type EditorTool = 'text' | 'image' | 'shape' | 'line' | 'icon' | 'table' | 'chart';

interface EditorToolbarProps {
  activeTool: EditorTool | null;
  onToolChange: (tool: EditorTool) => void;
  libraryActive?: boolean;
  onOpenLibrary: () => void;
  onInsertFromFile?: (file: File) => void;
  selectedObject: SlideObject | null;
  styleLocked?: boolean;
  contentLocked?: boolean;
  onAddText: () => void;
  onAddTable?: () => void;
  onAddChart?: () => void;
  onTableGrid?: (action: 'add-row' | 'remove-row' | 'add-col' | 'remove-col' | 'toggle-header') => void;
  onChartEdit?: (action: 'kind' | 'legend' | 'values' | 'add-series' | 'remove-series' | 'add-category' | 'remove-category', kind?: ChartKind) => void;
  readOnly?: boolean;
  onAddShape: (shape: NonNullable<SlideObjectStyle['shape']>, extras?: Partial<SlideObjectStyle>) => void;
  onStyle: (patch: Record<string, unknown>) => void;
  onToggleList: (kind: ListKind) => void;
  onApplyTextStyle: (step: TextStyleStep) => void;
  onReplaceImage?: () => void;
  onCropImage?: () => void;
  onFitImage?: () => void;
  onFitSlot?: () => void;
  onAdapt?: () => void;
  onAiRewrite?: () => void;
  onAiGrammar?: () => void;
  onAiTranslate?: (language?: 'sv' | 'fi' | 'en') => void;
  translateLanguage?: 'sv' | 'fi' | 'en';
  translateLanguages?: Array<{ id: 'sv' | 'fi' | 'en'; label: string }>;
  onTranslateLanguageChange?: (language: 'sv' | 'fi' | 'en') => void;
  showSmartLayout?: boolean;
  canFitSlot?: boolean;
  canAdapt?: boolean;
  showAi?: boolean;
  aiBusy?: boolean;
  queryTextMark?: (mark: 'bold' | 'italic' | 'underline') => boolean | null;
  queryTextStyle?: (key: 'fontFamily' | 'fontSize' | 'color' | 'fontWeight') => { value: string | number | null; mixed: boolean };
  onToggleMark?: (mark: 'bold' | 'italic' | 'underline') => void;
}

const EditorToolbar: React.FC<EditorToolbarProps> = ({
  activeTool,
  onToolChange,
  libraryActive: _libraryActive,
  onOpenLibrary,
  onInsertFromFile,
  selectedObject,
  styleLocked,
  contentLocked,
  onAddText,
  onAddTable,
  onAddChart,
  onTableGrid,
  onChartEdit,
  readOnly,
  onAddShape,
  onStyle,
  onToggleList,
  onApplyTextStyle,
  onReplaceImage,
  onCropImage,
  onFitImage,
  onFitSlot,
  onAdapt,
  onAiRewrite,
  onAiGrammar,
  onAiTranslate,
  translateLanguage,
  translateLanguages,
  onTranslateLanguageChange,
  showSmartLayout,
  canFitSlot = false,
  canAdapt = false,
  showAi,
  aiBusy,
  queryTextMark,
  queryTextStyle,
  onToggleMark,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [tableMoreAnchor, setTableMoreAnchor] = useState<HTMLElement | null>(null);
  const [translateAnchor, setTranslateAnchor] = useState<HTMLElement | null>(null);
  const pickLocalFile = () => fileInputRef.current?.click();
  const selectedTable = selectedObject?.type === 'table' ? normalizeTable(selectedObject.table) : null;
  const tableRows = selectedTable?.rows.length || 0;
  const tableCols = selectedTable?.colWidths.length || 0;

  return (
    <Box sx={{ bgcolor: PRES.white, borderBottom: `1px solid ${PRES.line}`, flexShrink: 0 }} data-presentation-toolbar="true">
      <Box sx={{ display: 'flex', alignItems: 'center', height: 44, px: 1.5, gap: 0.25 }}>
        <InsertButton label="Text" active={activeTool === 'text'} onClick={() => { onToolChange('text'); onAddText(); }}>
          <InsertTextIcon />
        </InsertButton>
        <InsertButton label="Bildbibliotek" active={activeTool === 'image'} onClick={() => onToolChange('image')}>
          <InsertImageIcon />
        </InsertButton>
        {onInsertFromFile && (
          <>
            <input
              ref={fileInputRef}
              hidden
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif,.png,.jpg,.jpeg,.webp,.gif"
              aria-label={COPY.insertFromFile}
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (file) onInsertFromFile(file);
              }}
            />
            <InsertButton label={COPY.insertFromFile} onClick={pickLocalFile}>
              <InsertFileIcon />
            </InsertButton>
          </>
        )}
        <InsertButton label="Ikon" active={activeTool === 'icon'} onClick={() => onToolChange('icon')}>
          <InsertSymbolIcon />
        </InsertButton>
        <ToolbarDivider />
        <InsertButton label="Former" active={activeTool === 'shape'} onClick={() => onToolChange('shape')}>
          <InsertShapeIcon />
        </InsertButton>
        <InsertButton label="Linje" active={activeTool === 'line'} onClick={() => onToolChange('line')}>
          <SolidLineIcon size={20} />
        </InsertButton>
        {!readOnly && (onAddTable || onAddChart) && <ToolbarDivider />}
        {!readOnly && onAddTable && (
          <InsertButton
            label={COPY.table}
            active={activeTool === 'table'}
            onClick={() => { onToolChange('table'); onAddTable(); }}
          >
            <TableHeaderIcon />
          </InsertButton>
        )}
        {!readOnly && onAddChart && (
          <InsertButton
            label={COPY.chart}
            active={activeTool === 'chart'}
            onClick={() => { onToolChange('chart'); onAddChart(); }}
          >
            <ChartColumnIcon size={20} />
          </InsertButton>
        )}
        {showAi && selectedObject && hasEditableText(selectedObject) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, ml: 'auto', flexShrink: 0 }}>
            <ToolbarDivider />
            <ToolbarIconButton label={COPY.aiRewrite} disabled={aiBusy} onClick={() => onAiRewrite?.()}>
              <AutoFixHighOutlinedIcon sx={{ fontSize: 20 }} />
            </ToolbarIconButton>
            <ToolbarIconButton label={COPY.aiGrammar} disabled={aiBusy} onClick={() => onAiGrammar?.()}>
              <SpellcheckIcon sx={{ fontSize: 20 }} />
            </ToolbarIconButton>
            {onAiTranslate && (
              <>
                <ToolbarIconButton
                  label={`${COPY.aiTranslateTo} ${translateLanguages?.find((item) => item.id === translateLanguage)?.label || ''}`.trim()}
                  disabled={aiBusy}
                  selected={Boolean(translateAnchor)}
                  onClick={(event) => {
                    if (translateLanguages?.length) setTranslateAnchor(event.currentTarget);
                    else onAiTranslate();
                  }}
                >
                  <TranslateIcon sx={{ fontSize: 20 }} />
                </ToolbarIconButton>
                <Menu
                  anchorEl={translateAnchor}
                  open={Boolean(translateAnchor)}
                  onClose={() => setTranslateAnchor(null)}
                  MenuListProps={{ dense: true, 'aria-label': COPY.aiTranslate }}
                >
                  {(translateLanguages || []).map((item) => (
                    <MenuItem
                      key={item.id}
                      selected={item.id === translateLanguage}
                      onClick={() => {
                        onTranslateLanguageChange?.(item.id);
                        onAiTranslate(item.id);
                        setTranslateAnchor(null);
                      }}
                    >
                      {COPY.aiTranslateTo} {item.label}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          alignContent: 'center',
          height: 'auto',
          minHeight: 48,
          maxHeight: 96,
          px: 1.5,
          py: 0.5,
          boxSizing: 'border-box',
          overflow: 'hidden',
          flexWrap: 'wrap',
          rowGap: 0.25,
          bgcolor: PRES.cream,
          borderTop: `1px solid ${PRES.line}`,
        }}
      >
        {activeTool === 'shape' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
            <ToolbarIconButton label="Rektangel" onClick={() => onAddShape('rect')}>
              <ShapeRectangleIcon />
            </ToolbarIconButton>
            <ToolbarIconButton label="Rundad rektangel" onClick={() => onAddShape('roundRect')}>
              <ShapeRoundedRectangleIcon />
            </ToolbarIconButton>
            <ToolbarIconButton label="Ellips" onClick={() => onAddShape('ellipse')}>
              <ShapeEllipseIcon />
            </ToolbarIconButton>
            <ToolbarIconButton label="Triangel" onClick={() => onAddShape('triangle')}>
              <ShapeTriangleIcon />
            </ToolbarIconButton>
          </Box>
        )}

        {activeTool === 'line' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
            <ToolbarIconButton label="Heldragen" onClick={() => onAddShape('line')}>
              <SolidLineIcon size={22} />
            </ToolbarIconButton>
            <ToolbarIconButton label="Streckad" onClick={() => onAddShape('line', { strokeDash: 'dashed' })}>
              <DashedLineIcon size={22} />
            </ToolbarIconButton>
            <ToolbarIconButton label="Prickad" onClick={() => onAddShape('line', { strokeDash: 'dotted' })}>
              <DottedLineIcon size={22} />
            </ToolbarIconButton>
          </Box>
        )}

        {(activeTool === 'image' || activeTool === 'icon') && (
          <ImageTray
            object={selectedObject?.type === 'image' || selectedObject?.type === 'product' ? selectedObject : null}
            disabled={styleLocked}
            onOpenLibrary={onOpenLibrary}
            onPickFromFile={onInsertFromFile ? pickLocalFile : undefined}
            onReplaceImage={onReplaceImage}
            onCropImage={onCropImage}
            onFitImage={onFitImage}
            onStyle={onStyle}
            queryTextMark={queryTextMark}
            queryTextStyle={queryTextStyle}
            onToggleMark={onToggleMark}
            onToggleList={onToggleList}
          />
        )}

        {selectedObject?.type === 'table' && !readOnly && onTableGrid && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mr: 0.5, flexShrink: 0 }}>
            <ToolbarIconButton
              label={COPY.tableHeaderRow}
              selected={selectedTable?.headerRow !== false}
              disabled={contentLocked}
              onClick={() => onTableGrid('toggle-header')}
            >
              <TableHeaderIcon />
            </ToolbarIconButton>
            <ToolbarIconButton
              label={COPY.tableAddRow}
              disabled={contentLocked || tableRows >= TABLE_MAX_ROWS}
              onClick={() => onTableGrid('add-row')}
            >
              <TableAddRowIcon />
            </ToolbarIconButton>
            <ToolbarIconButton
              label={COPY.tableAddCol}
              disabled={contentLocked || tableCols >= TABLE_MAX_COLS}
              onClick={() => onTableGrid('add-col')}
            >
              <TableAddColumnIcon />
            </ToolbarIconButton>
            <ToolbarIconButton
              label={COPY.moreActions}
              disabled={contentLocked}
              selected={Boolean(tableMoreAnchor)}
              onClick={(event) => setTableMoreAnchor(event.currentTarget)}
            >
              <MoreHorizIcon fontSize="small" />
            </ToolbarIconButton>
            <Menu
              anchorEl={tableMoreAnchor}
              open={Boolean(tableMoreAnchor)}
              onClose={() => setTableMoreAnchor(null)}
              MenuListProps={{ dense: true, 'aria-label': COPY.moreActions }}
            >
              <MenuItem
                disabled={contentLocked || tableRows <= 1}
                onClick={() => {
                  onTableGrid('remove-row');
                  setTableMoreAnchor(null);
                }}
              >
                <Box sx={{ mr: 1, display: 'flex' }}><TableRemoveRowIcon /></Box>
                {COPY.tableRemoveRow}
              </MenuItem>
              <MenuItem
                disabled={contentLocked || tableCols <= 1}
                onClick={() => {
                  onTableGrid('remove-col');
                  setTableMoreAnchor(null);
                }}
              >
                <Box sx={{ mr: 1, display: 'flex' }}><TableRemoveColumnIcon /></Box>
                {COPY.tableRemoveCol}
              </MenuItem>
            </Menu>
            <ToolbarDivider />
          </Box>
        )}

        {selectedObject?.type === 'chart' && !readOnly && onChartEdit && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mr: 1, flexShrink: 0 }}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={selectedObject.chart?.kind || 'column'}
              disabled={styleLocked}
              onChange={(_event, value: ChartKind | null) => value && onChartEdit('kind', value)}
              sx={toolbarToggleSx}
              aria-label={COPY.chart}
            >
              {([
                { id: 'column' as const, label: COPY.chartColumn, icon: <ChartColumnIcon /> },
                { id: 'bar' as const, label: COPY.chartBarShort, icon: <ChartBarIcon /> },
                { id: 'line' as const, label: COPY.chartLine, icon: <ChartLineIcon /> },
                { id: 'pie' as const, label: COPY.chartPie, icon: <ChartPieIcon /> },
              ]).map((item) => (
                <ToggleButton key={item.id} value={item.id} aria-label={item.label}>
                  <Tooltip title={item.label} arrow>
                    <Box component="span" sx={{ display: 'flex' }}>{item.icon}</Box>
                  </Tooltip>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <ToolbarDivider />
            <ToggleButtonGroup size="small" sx={toolbarToggleSx}>
              <ToggleButton
                value="legend"
                aria-label={COPY.chartLegend}
                selected={selectedObject.chart?.showLegend !== false}
                disabled={styleLocked}
                onClick={() => onChartEdit('legend')}
              >
                <Tooltip title={COPY.chartLegend} arrow>
                  <Box component="span" sx={{ display: 'flex' }}><ChartLegendIcon /></Box>
                </Tooltip>
              </ToggleButton>
              <ToggleButton
                value="values"
                aria-label={COPY.chartShowValues}
                selected={Boolean(selectedObject.chart?.showValues)}
                disabled={styleLocked}
                onClick={() => onChartEdit('values')}
              >
                <Tooltip title={COPY.chartShowValues} arrow>
                  <Box component="span" sx={{ display: 'flex' }}><ChartValuesIcon /></Box>
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        {activeTool !== 'shape' && activeTool !== 'line' && activeTool !== 'image' && activeTool !== 'icon' && selectedObject && selectedObject.type !== 'chart' && (
          <SelectionToolbar
            object={selectedObject}
            disabled={styleLocked}
            onStyle={onStyle}
            queryTextMark={queryTextMark}
            queryTextStyle={queryTextStyle}
            onToggleMark={onToggleMark}
            onToggleList={onToggleList}
            onApplyTextStyle={onApplyTextStyle}
            onReplaceImage={onReplaceImage}
            onCropImage={onCropImage}
            onFitImage={onFitImage}
          />
        )}

        {activeTool === 'text' && selectedObject?.type !== 'text' && selectedObject?.type !== 'shape' && (
          <Typography sx={{ color: PRES.muted, fontSize: 13 }}>
            En textruta är tillagd. Klicka i den för att skriva. Fonter och storlek syns här när texten är markerad.
          </Typography>
        )}

        {!activeTool && !selectedObject && (
          <Typography sx={{ color: PRES.muted, fontSize: 13 }}>
            Välj ett verktyg ovan. Inställningar för text, bild och former visas i den här raden.
          </Typography>
        )}
        {showSmartLayout && selectedObject && (
          <Box sx={{ display: 'flex', gap: 1, ml: 1 }}>
            <Tooltip title={canFitSlot ? COPY.fitSlotHelp : COPY.fitSlotUnavailable}>
              <Box component="span">
                <Button size="small" disabled={!canFitSlot} onClick={onFitSlot} sx={{ textTransform: 'none' }}>{COPY.fitSlot}</Button>
              </Box>
            </Tooltip>
            <Tooltip title={canAdapt ? COPY.adaptStylesHelp : COPY.adaptStylesUnavailable}>
              <Box component="span">
                <Button size="small" disabled={!canAdapt} onClick={onAdapt} sx={{ textTransform: 'none' }}>{COPY.adaptStyles}</Button>
              </Box>
            </Tooltip>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const ImageTray: React.FC<{
  object: SlideObject | null;
  disabled?: boolean;
  onOpenLibrary: () => void;
  onPickFromFile?: () => void;
  onReplaceImage?: () => void;
  onCropImage?: () => void;
  onFitImage?: () => void;
  onStyle: (patch: Record<string, unknown>) => void;
  queryTextMark?: (mark: 'bold' | 'italic' | 'underline') => boolean | null;
  queryTextStyle?: (key: 'fontFamily' | 'fontSize' | 'color' | 'fontWeight') => { value: string | number | null; mixed: boolean };
  onToggleMark?: (mark: 'bold' | 'italic' | 'underline') => void;
  onToggleList: (kind: ListKind) => void;
}> = ({ object, disabled, onOpenLibrary, onPickFromFile, onReplaceImage, onCropImage, onFitImage, onStyle, queryTextMark, queryTextStyle, onToggleMark, onToggleList }) => {
  if (!object) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography sx={{ color: PRES.textSecondary, fontFamily: 'inherit', fontSize: 13 }}>
          Välj en fil i biblioteket till höger, eller infoga från din dator.
        </Typography>
        <Button
          size="small"
          onClick={onOpenLibrary}
          sx={{ textTransform: 'none', bgcolor: PRES.interactive, color: PRES.white, '&:hover': { bgcolor: PRES.interactiveHover } }}
        >
          Öppna bibliotek
        </Button>
        {onPickFromFile && (
          <Button size="small" onClick={onPickFromFile} sx={{ textTransform: 'none' }}>
            {COPY.insertFromFile}
          </Button>
        )}
      </Box>
    );
  }

  return (
    <SelectionToolbar
      object={object}
      disabled={disabled}
      onStyle={onStyle}
      queryTextMark={queryTextMark}
      queryTextStyle={queryTextStyle}
      onToggleMark={onToggleMark}
      onToggleList={onToggleList}
      onReplaceImage={onReplaceImage}
      onCropImage={onCropImage}
      onFitImage={onFitImage}
    />
  );
};

export default EditorToolbar;
